import { useEffect, useState } from 'react';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { usePlaidLink } from 'react-plaid-link';
import { Alert } from 'reactstrap';
import Button from 'reactstrap/lib/Button';
import * as Sentry from '@sentry/react';
import { useFormikContext } from 'formik';

import Card from 'Lib/components/common/Card';
import Toggle from 'Lib/components/form/Toggle';

import VerificationDocuments from './components/VerificationDocuments';

const PlaidClient = ({ providerToken, onComplete, message }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const {
        setFieldValue,
        values: { identityVerification: verificationData }
    } = useFormikContext();

    useEffect(() => {
        if (isOpen && ready && providerToken) {
            console.log('opening client');
            open();
        }
    }, [providerToken, isOpen, ready]);

    const skipVerification = verificationData?.skipVerification ?? false;

    const plaidConfig = {
        onSuccess: public_token => {
            handlePlaidCompleted(public_token);
        },
        onExit: error => {
            setIsOpen(false);
            if (error) {
                handlePlaidError(error);
            }
        },
        onEvent: () => {},
        onLoad: () => {},
        token: providerToken
    };

    const handlePlaidError = () => {
        setHasErrors(true);
        setFieldValue('identityVerification', { completed: false, skipVerification: true });
    };

    const { open, ready } = usePlaidLink(plaidConfig);

    const handlePlaidCompleted = () => {
        onComplete();
        setFieldValue('identityVerification', { completed: true, skipVerification: false });
    };

    const handleSkipVerification = () => {
        setFieldValue('identityVerification', { completed: false, skipVerification: !skipVerification });
    };

    const openClient = () => {
        setIsOpen(true);
        setFieldValue('identityVerification', { ...verificationData, skipVerification: false });
    };

    return (
        <>
            {hasErrors ? (
                <Alert color="danger">An error occurred and we are unable to process your identity verification. Please proceed to the next step.</Alert>
            ) : (
                <>
                    {message && <FroalaEditorView model={message} />}
                    <p>Rentvine uses Plaid to verify your identity. Plaid is used to verify the authenticity of the identity documents uploaded by the Applicant.</p>
                    <Button onClick={openClient} disabled={!ready} color="primary">
                        Start Verification
                    </Button>
                </>
            )}
            <hr style={{ margin: '1.4rem 0' }} />
            <div style={{ display: 'flex' }}>
                <Toggle onChange={handleSkipVerification} value={skipVerification} disabled={hasErrors} />
                <span style={{ color: '#808080', marginLeft: '12px' }}>Opt-out of Plaid verification and manually upload identification documents</span>
            </div>
        </>
    );
};

const VerificationCompleted = () => {
    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <i className="fas fa-check-circle" style={{ color: '#46b449', marginRight: '6px' }} />
            <p style={{ margin: '0' }}>Your identity verification has been completed</p>
        </div>
    );
};

const IdentityVerification = ({ completed, providerToken, onComplete, message }) => {
    return (
        <Card title="Identity Verification">
            {completed ? <VerificationCompleted /> : providerToken && <PlaidClient providerToken={providerToken} onComplete={onComplete} message={message} />}
        </Card>
    );
};

export default IdentityVerification;
